import { spaceJSONText } from "../../../constants/misc";
import queryString from "query-string";

export const parseTryouts = (items) => {
  if (items === undefined) return [];

  // Handle Immutable.js
  if (typeof items.valueSeq === "function") {
    return items
      .valueSeq()
      .toArray()
      .map((path) => parseTryoutPath(path));
  }

  // Handle plain object
  if (typeof items === "object") {
    return Object.values(items).map((path) => parseTryoutPath(path));
  }

  return [];
};

export const parseTryoutPath = (path) => {
  if (!path) return [];

  // Handle Immutable.js
  if (typeof path.valueSeq === "function") {
    return path
      .valueSeq()
      .toArray()
      .map((method) => parseTryout(method));
  }

  // Handle plain object
  if (typeof path === "object") {
    return Object.values(path).map((method) => parseTryout(method));
  }

  return [];
};

export const parseTryout = (item) => {
  if (item === undefined) {
    console.log("[TRYOUT DEBUG] parseTryout: item is undefined");
    return {};
  }

  // Helper to get value from either Immutable or plain object
  const getValue = (key) => {
    if (typeof item.get === "function") {
      return item.get(key);
    }
    return item[key];
  };

  // Helper to convert to plain JS if needed
  const toPlainJS = (value) => {
    if (value && typeof value.toJS === "function") {
      return value.toJS();
    }
    return value;
  };

  const form = getValue("form");
  const historyForm = getValue("historyForm");
  const formErrors = getValue("formErrors");
  const response = getValue("response");
  const history = getValue("history");

  const result = {
    ...parseDefinitions(item),
    path: getValue("path"),
    method: getValue("method"),
    https: getValue("https"),
    loading: getValue("loading"),
    response: toPlainJS(response),
    headers: getValue("headers"),
    formShown: getValue("formShown"),
    toggleForm: getValue("toggleForm"),
    form: toPlainJS(form) || TRYOUT_STATE_FORM,
    historyForm: toPlainJS(historyForm) || TRYOUT_STATE_FORM,
    history: toPlainJS(history),
    formErrors: toPlainJS(formErrors) || [],
    documentationId: getValue("documentationId"),
    horLayout: getValue("horLayout"),
    imported: getValue("imported"),
    fromTour: getValue("fromTour"),
    description: getValue("description"),
    minified: getValue("minified"),
    timestamp: getValue("timestamp"), // Include timestamp
  };

  console.log("[TRYOUT DEBUG] parseTryout result:", {
    path: result.path,
    method: result.method,
    formShown: result.formShown,
    toggleForm: result.toggleForm,
    hasForm: !!result.form,
    hasParameters: !!result.parameters,
    timestamp: result.timestamp,
  });

  return result;
};

export const parseDefinitions = (item) => {
  if (!item) return { parameters: [], security: [] };

  // Helper to get value from either Immutable or plain object
  const getValue = (key) => {
    if (typeof item.get === "function") {
      return item.get(key);
    }
    return item[key];
  };

  // Helper to convert to plain JS if needed
  const toPlainJS = (value) => {
    if (value && typeof value.toJS === "function") {
      return value.toJS();
    }
    return value;
  };

  const parameters = getValue("parameters");
  const security = getValue("security");

  return {
    parameters: toPlainJS(parameters) || [],
    security: toPlainJS(security) || [],
  };
};

export const parseProperties = (schema) => {
  function parseProperties(schema) {
    const { properties, example } = schema;
    let response = {};
    Object.keys(properties).map((key) => {
      const propExample =
        example && typeof example[key] !== "undefined"
          ? example[key]
          : undefined;
      return (response = properties[key].properties
        ? {
            ...response,
            [key]: parseProperties(properties[key], propExample),
          }
        : {
            ...response,
            [key]: hasExample(properties[key], propExample)
              ? parseExample(properties[key], propExample)
              : hasExample(properties[key], propExample)
              ? parseExample(properties[key], propExample)
              : properties[key].format === "date-time"
              ? new Date().toISOString()
              : properties[key].type === "boolean"
              ? true
              : properties[key].type,
          });
    });
    return response;
  }

  const { allOf } = schema;
  if (allOf) {
    return allOf.reduce((result, current) => {
      return {
        ...result,
        ...parseProperties(current),
      };
    }, {});
  }

  return parseProperties(schema);
};

const hasExample = (property, propExample) => {
  return property.example || typeof propExample !== "undefined";
};

const parseExample = (property, propExample) => {
  let result = property.example ? property.example : propExample;
  if (property.type === "object") {
    try {
      result = JSON.parse(result, undefined, 4);
    } catch (e) {
      return result;
    }
  }
  return result;
};

// Resolve local JSON pointers ("#/components/schemas/X") so parseProperties sees real schemas.
// The live portal dereferenced the whole document with swagger-parser before building tryouts;
// Redoc itself keeps the original (it resolves refs on its own). Cycles are cut by leaving an
// empty object where a schema references one of its own ancestors.
export const dereferenceLocalRefs = (spec) => {
  if (!spec || typeof spec !== "object") return spec;
  const lookup = (ref) =>
    ref
      .replace(/^#\//, "")
      .split("/")
      .map((seg) => seg.replace(/~1/g, "/").replace(/~0/g, "~"))
      .reduce((node, seg) => (node == null ? undefined : node[seg]), spec);
  const walk = (node, stack) => {
    if (Array.isArray(node)) return node.map((item) => walk(item, stack));
    if (!node || typeof node !== "object") return node;
    if (typeof node.$ref === "string" && node.$ref.startsWith("#/")) {
      if (stack.includes(node.$ref)) return {}; // circular
      const target = lookup(node.$ref);
      return target === undefined ? node : walk(target, [...stack, node.$ref]);
    }
    const out = {};
    Object.keys(node).forEach((key) => {
      out[key] = walk(node[key], stack);
    });
    return out;
  };
  return walk(spec, []);
};

export const parseBody = (method, path, swaggerJson) => {
  // Check if the path and method exist in swagger
  if (!swaggerJson?.paths?.[path]?.[method]) {
    return null;
  }

  const body = swaggerJson["paths"][path][method].requestBody;

  // If no requestBody, return null
  if (!body) {
    return null;
  }

  try {
    const types = ["application/x-www-form-urlencoded", "application/json"];
    const props = body.content;
    for (const type of types) {
      if (props[type]) {
        const { discriminator } = props[type].schema;
        return {
          body: spaceJSONText(parseProperties(props[type].schema)),
          discriminator:
            discriminator && discriminator.mapping
              ? Object.keys(discriminator.mapping).reduce(
                  (discriminatorProperties, key) => {
                    return {
                      ...discriminatorProperties,
                      [key]: spaceJSONText(
                        parseProperties(discriminator.mapping[key])
                      ),
                    };
                  },
                  {}
                )
              : undefined,
        };
      }
    }
  } catch (e) {
    return {
      body: null,
      discriminator: undefined,
    };
  }
  return {
    body: null,
    discriminator: undefined,
  };
};

export const getRedocServerUrl = (server) => {
  let { url, variables } = server;
  if (variables) {
    Object.keys(variables).forEach((var_name) => {
      url = url.replace("{" + var_name + "}", variables[var_name].default);
    });
  }
  return url;
};

export const getData = (path, method, form, swaggerJson, authUrl = null) => {
  const query = queryString.stringify(form.query);
  let body = null;
  try {
    if (form.body) {
      const {
        body: { selector, discriminator },
      } = form;
      body =
        discriminator && selector ? discriminator[selector] : form.body.body;
      body = JSON.parse(body);
    }
  } catch (e) {
    body = undefined;
  }
  Object.keys(form.path).map((key) => {
    path = path.replace("{" + key + "}", form.path[key]);
  });
  const url = !authUrl ? getRedocServerUrl(swaggerJson.servers[0]) : authUrl;

  let result = url + path;
  result += query !== "" ? "?" + query : query;
  return {
    url: result,
    data: body ? JSON.stringify(body) : null,
    dataType: "json",
    headers: {
      ...form.header,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    type: method,
  };
};

export const TRYOUT_STATE_FORM = {
  query: {},
  header: {},
  path: {},
  body: "",
};

export const TRYOUT_STATE = {
  path: "",
  method: "",
  parameters: [],
  history: [],
  security: [],
  swaggerJson: {},
  https: true,
  loading: false,
  response: null,
  headers: {},
  formShown: false,
  toggleForm: null,
  form: TRYOUT_STATE_FORM,
  formErrors: null,
  horLayout: false,
};
